/* Core & libraries */
import React, {useCallback, useEffect, useState} from 'react'
import Dropzone from 'react-dropzone-uploader-with-credentials'
import { useTheme } from "react-jss";
import Row from "react-bootstrap/Row";
import { FormattedMessage, useIntl } from "react-intl";
import Col from "react-bootstrap/Col";

/* Helpers, constants, custom methods, internal libraries */
import { useNotificationContext } from "../../../../contexts/notificationContext";
import { useSpinnersUpdateContext } from '../../../../contexts/spinnersContext';
import { useUserContext } from "../../../../contexts/userContext";
import { getCookieValue } from "../../../../helpers/general";
import { ATM_REDEMPTIONS_UPLOAD } from "../../../../endpoints/endpoints";
import { API_HOST, API_PROTOCOL } from "../../../../constants/appConstants";
import {useNavigate} from "react-router";
import {useLocation} from "react-router-dom";
import { COMPLETED_LOYALTY_ROUTE } from "../../../../constants/routes";

/* Components */
import UCButton from "../../../buttons";
import CustomPreview from "./customPreview/customPreview";
import CustomInput from "./customInput/customInput";
import CustomSubmitButton from "./customSubmitButton/customSubmitButton";

/* Styles */
import styles from "./styles";

const AtmBulkImport = (props) => {

  const { isUploading, setIsUploading, setShowAtmBulkImportModal, allotmentId, setAssignModalShow, onRedeemCompleted } = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();

  const { setNotification } = useNotificationContext();
  const setSpinner = useSpinnersUpdateContext();
  const navigate = useNavigate();
  const location = useLocation();

  const userContext = useUserContext();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId

  const [nameColumnPresent, setNameColumnPresent] = useState(false);
  const [maskedColumnPresent, setMaskedColumnPresent] = useState(false);
  const [advancedNumberColumnPresent, setAdvancedNumberColumnPresent] = useState(false);
  const [tierColumnPresent, setTierColumnPresent] = useState(false);
  const [correctNumberOfCommas, setCorrectNumberOfCommas] = useState(true);
  const [fileSize, setFileSize] = useState("");
  const [maxTravelersNumber, setMaxTravelersNumber] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emptyRows, setEmptyRows] = useState(false);

  useEffect(() => {
    setSpinner({
      ["uploadFileSpinner"]: false
    })
  }, []);

  // specify upload params and url for your files
  const getUploadParams = ({ meta, file }) => {
    const body = new FormData()
    body.append('file', file)
    return {
      url: API_PROTOCOL + "://" + API_HOST + ATM_REDEMPTIONS_UPLOAD.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId).replace("{allotmentId}", allotmentId),
      body,
      headers: {
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }
    }
  }

  // called every time a file's `status` changes
  const handleChangeStatus = ({ meta, file, xhr }, status) => {
    if (status === "uploading") {
      setSpinner({
        ["uploadFileSpinner"]: true
      })
      setIsUploading(true);
    } else if (["error", "headers_received", "exception", "error_upload"].includes(status)) {
      setIsUploading(false);
    }

    if (['exception_upload', 'error_upload'].includes(status)) {
      xhr.onload = function () {
        if(xhr?.status === 403) {
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadAllotment.errorTitle" />,
            <FormattedMessage id="notifications.uploadAllotment.7014" />
          );
          setErrorMessage(<FormattedMessage id="notifications.uploadAllotment.7014" />)
        } else if (xhr?.status === 500) {
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "7021") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.uploadAllotment.error.7021" />)
          } else{
            setNotification(
              "error",
              <FormattedMessage id="notifications.uploadAllotment.errorTitle" />,
              <FormattedMessage id="notifications.uploadPeople.403" />
            );
            setErrorMessage(<FormattedMessage id="notifications.uploadPeople.403" />)
          }
        } else if(xhr?.status === 400) {
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "7015") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.7015" />)
          }
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "7016") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.7016" />)
          }
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "7018") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.7018" />)
          }
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "7019") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.7019" />)
          }
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "7020") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.7020" />)
          }
          if(JSON.parse(xhr?.response)?.errors[0].errorCode === "10001") {
            setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.10001" />)
          }

        } else {
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadAllotment.errorTitle" />,
            <FormattedMessage id="loyalty.atmBulkImport.notifications.description" />,
          );
          setErrorMessage(<FormattedMessage id="loyalty.atmBulkImport.notifications.tryAgain" />)
        }
      };
      setSpinner({
        ["uploadFileSpinner"]: false
      })
    }

    if (status === "preparing") {
      return new Promise((resolve) => {
        const regexp = /^[a-zA-Z0-9-_. ()]+$/;
        if (file.name.search(regexp) === -1) {
          resolve(intl.formatMessage({ id: "dropzone.invalidName" }));
        } else {
          let reader = new FileReader();
          reader.onloadend = () => {
            let lines = reader.result.split('\n').map(line => line.trim()).filter(Boolean);
            if (lines.length > 1) {
              let columns = lines[0].split(',').map(col => col.trim().toLowerCase());
              setNameColumnPresent(columns.includes("name"))
              setMaskedColumnPresent(columns.includes("masked aadvantage® number"))
              setAdvancedNumberColumnPresent(columns.includes("aadvantage® number"))
              setTierColumnPresent(columns.includes("tier"))
              setFileSize((meta.size / 1024).toFixed(3))
              let numberOfCommas = lines[0].split(',').length - 1;
              meta.nameColumnPresent = nameColumnPresent; // <-- Add this line
              meta.maskedColumnPresent = maskedColumnPresent; // <-- Add this line
              meta.advancedNumberColumnPresent = advancedNumberColumnPresent; // <-- Add this line
              meta.tierColumnPresent = tierColumnPresent; // <-- Add this line
              meta.correctNumberOfCommas = correctNumberOfCommas
              if (lines.length > 500) {
                setMaxTravelersNumber(true)
              } else { setMaxTravelersNumber(false) }

              const checklines = reader.result.split("\n").map((line) => line.trim());
              const headers = checklines[0].split(",").map((header) => header.trim());
              const emptyRowsList = [];
              const columnErrors = [];

              for (let i = 1; i < checklines.length; i++) {
                const row = checklines[i].split(",").map((value) => value.trim());
                const emptyColumns = [];

                if (row.length === headers.length) {
                  // Check for empty columns
                  row.forEach((value, index) => {
                    if (!value) {
                      emptyColumns.push(headers[index]);
                    }
                  });

                  if (emptyColumns.length > 0) {
                    setEmptyRows(true)
                    columnErrors.push({
                      rowIndex: i,
                      emptyColumns,
                    });
                  } else {
                    setEmptyRows(false)
                  }
                } else {
                  emptyRowsList.push(i);
                }
              }

              for (let i = 1; i < lines.length; i++) {
                if (lines[i].split(',').length - 1 !== numberOfCommas) {
                  setCorrectNumberOfCommas(false)
                  return;
                } else {
                  setCorrectNumberOfCommas(true)
                  return
                }
              }
              meta.numberOfInvites = lines.length - 1;
              resolve(false); // No error, file is valid.
            }
          }


          let blob = new Blob([file], { type: file.type });
          reader.readAsText(blob);
        }
      }).then((errorMessage) => {
        if (errorMessage) {
          setSpinner({
            ["uploadFileSpinner"]: false
          })
          setIsUploading(false);
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadAllotment.errorTitle" />,
            errorMessage
          );
          return;
        }
      });
    } else if (status === "done") {
      setSpinner({
        ["uploadFileSpinner"]: false
      })

      setIsUploading(false);
      setNotification(
        "success",
        <FormattedMessage id="admin.loyalty.notificationSuccessTitle" />,
        <FormattedMessage id="admin.loyalty.notificationSuccessDescription" />
      );
      setShowAtmBulkImportModal(false)
      setAssignModalShow(false);
      if (typeof onRedeemCompleted === 'function') onRedeemCompleted();
    } else if (status === "error") {
    }
  }

  // receives array of files that are done uploading when submit button is clicked
  const handleSubmit = (files, allFiles) => {
    allFiles.forEach(f => f.restart())
  }
  const customLayout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }) => {
    return (
      <div>
        {previews}
        {
          files.length < maxFiles &&
          <div {...dropzoneProps}>
            {input}
          </div>
        }
        <Row className={classes.buttonsWrapper}>
          <Col className={classes.colCancel}>
            <UCButton
              onClick={() => setShowAtmBulkImportModal(false)}
              type="default"
              className={classes.button}
              text={<FormattedMessage id="peopleHeader.modalUpload.cancel" />}
              disabled={isUploading}
            />
          </Col>
          <Col className={classes.colSubmit}>
            {submitButton}
          </Col>
        </Row>
      </div>
    )
  }

  const maxSize = 5242880;
  //const minSize = 15;

  return (
    <Dropzone
      autoUpload={false}
      onChangeStatus={handleChangeStatus}
      getUploadParams={getUploadParams}
      LayoutComponent={customLayout}
      onSubmit={handleSubmit}
      maxFiles={1}
      accept=".csv"
      validate={(file) => {
        const regexp = /^[a-zA-Z0-9-_. ()]+$/;
        if (file.file.name.search(regexp) === -1) {
          return intl.formatMessage({ id: "dropzone.invalidName" });
        }
      }}
      maxSizeBytes={maxSize}
      //minSizeBytes={minSize}
      classNames={{ dropzone: classes.dropzone }}
      PreviewComponent={props =>
        <CustomPreview
          {...props}
          correctNumberOfCommas={correctNumberOfCommas}
          nameColumnPresent={nameColumnPresent}
          maskedColumnPresent={maskedColumnPresent}
          advancedNumberColumnPresent={advancedNumberColumnPresent}
          tierColumnPresent={tierColumnPresent}
          maxTravelersNumber={maxTravelersNumber}
          emptyRows={emptyRows}
          setIsUploading={setIsUploading}
          errorMessage={errorMessage}
          error
          fileSize={fileSize}
        />}
      InputComponent={props => <CustomInput {...props} />}
      SubmitButtonComponent={props =>
        <CustomSubmitButton
          {...props}
          correctNumberOfCommas={correctNumberOfCommas}
          nameColumnPresent={nameColumnPresent}
          maskedColumnPresent={maskedColumnPresent}
          advancedNumberColumnPresent={advancedNumberColumnPresent}
          tierColumnPresent={tierColumnPresent}
          maxTravelersNumber={maxTravelersNumber}
          emptyRows={emptyRows}
          buttonClassName={classes.button}
        />}
    />
  )
}

export default AtmBulkImport;